import { Spin } from 'antd';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig, container } from 'src/inversify.config';
import { formatMessage } from 'src/core/services/http.service';
import { ListState, Query } from 'src/core/stores/data-store';
import { clone } from 'src/core/utils/object';
import { RoleAssignmentSummary, RoleAssignmentSummaryStore } from 'src/stores/role-assignments-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';

import AddGroupRoleModal from './group-role-assignment-modal';
import { SearchOutlined } from '@ant-design/icons';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { LicenseCreationAttributesStore } from 'src/stores/licensing-store';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import moment from 'moment';
import { licenseManagerRoleName, licenseReviewerRoleName, StructmasterUserRoleName } from 'src/stores/roles-store';
import UpdateRoleAttributesDialog from 'src/pages/roles/role-attributes/update-role-attributes';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';
import { IdentityType, ScopeType } from 'src/common/types';
import { ValidatedSearch } from 'src/common/components/validated-search';
import { GroupType } from 'src/stores/groups-store';
import { isArray } from 'lodash';
import { OptionsFilter } from 'src/common/components/table-filters/options-filter';
import useMediaQuery from 'src/common/hooks/use-media-query';

const GroupRolesTab: FC<{ groupId: string; groupOrganizationId?: string; isPartnerOrg: boolean; groupName: string; organizationName: string }> = ({
  groupId,
  groupOrganizationId,
  isPartnerOrg,
  groupName,
  organizationName,
}) => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const groupRolesStore = container.get(RoleAssignmentSummaryStore);
  const groupRolesState = groupRolesStore.state;
  const [addRolesVisible, setAddRolesVisible] = useState(false);

  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const [hasManageGlobalLicensesPermission, checkingPermissions] = useCheckGlobalLicensesPermission();
  const [licenseAttrVisible, setLicenseAttrVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<RoleAssignmentSummary>();
  const [selectedRowsLength, setSelectedRowsLength] = useState<number>(0);
  const onMobile = useMediaQuery('(max-width: 768px)');

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
    skip: 0,
    take: 10,
    filter: [{ and: [`identityId eq ${groupId}`] }],
  } as Query);

  useEffect(() => {
    const newQuery = {
      searchQuery: '',
      orderBy: [{ field: 'Role', direction: 'Ascending', useProfile: true }],
      skip: 0,
      take: 10,
      filter: [{ and: [`identityId eq ${groupId}`] }],
    } as Query;
    setQuery(newQuery);
    groupRolesStore.load(newQuery);
  }, [groupId, groupRolesStore]);

  const onRefresh = () => {
    setSelectedAssignment(undefined);
    groupRolesStore.load(query);
  };

  const expirationDateFilter = (query.filter as any)['expirationDate'];
  const hasExpirationDateFilter =
    expirationDateFilter !== null &&
    expirationDateFilter !== undefined &&
    ((expirationDateFilter.ge !== null && expirationDateFilter.ge !== undefined) ||
      (expirationDateFilter.le !== null && expirationDateFilter.le !== undefined));
  const expirationDateHeader = t('ExpirationDate') + ' ' + (hasExpirationDateFilter ? '[*]' : '');
  const searchFilter = useRef<string>();

  const tableModel = {
    query,
    rowDisabled: (data) =>
      ((data.role === licenseManagerRoleName || data.role === licenseReviewerRoleName) && !hasManageGlobalLicensesPermission) ||
      data.role === StructmasterUserRoleName,
    columns: [
      {
        field: 'Role',
        sortable: true,
        sortField: 'Role',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter baseUrl={groupRolesStore.baseUrl} fieldName={'Role/Name'} fieldValue={'role'} filter={`identityId eq ${groupId}`} />
        ),
        fiterField: 'Role/Name',
        title: t('Role'),
        renderer: (data) => (
          <span
            className={
              ((data.role === licenseManagerRoleName || data.role === licenseReviewerRoleName) && !hasManageGlobalLicensesPermission) ||
              data.role === StructmasterUserRoleName
                ? 'hide-checkbox'
                : ''
            }
          >
            {data.role}
          </span>
        ),
      },
      {
        field: 'ScopeTitle',
        sortable: true,
        sortField: 'ScopeTitle',
        useProfile: true,
        searchable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={groupRolesStore.baseUrl}
            fieldName={'Scope/Title'}
            fieldValue={'scopeTitle'}
            filter={`identityId eq ${groupId}`}
          />
        ),
        fiterField: 'Scope/Title',
        title: t('Scope'),
        renderer: (data) => <span>{data.scopeTitle}</span>,
      },
      {
        field: 'ScopeType',
        sortable: true,
        sortField: 'ScopeType',
        useProfile: true,
        searchable: true,
        filter: <OptionsFilter allowSearch options={Object.values(ScopeType).map((scopeType) => ({ label: t(scopeType), value: scopeType }))} />,
        fiterField: 'Scope/Type',
        title: t('ScopeType'),
        renderer: (data) => <span>{t(data.scopeType)}</span>,
      },
      {
        field: 'RoleExpirationDate',
        sortable: true,
        searchable: false,
        title: t(expirationDateHeader),
        filter: <DateRangeFilter allowClear />,
        renderer: (data) => <span>{data.expirationDate ? moment(data.expirationDate).format(getDateFormat()) : ''}</span>,
      },
      {
        field: 'UpdateRoleAttributes',
        sortable: false,
        searchable: false,
        title: '',
        renderer: (data) =>
          ((data.role === licenseManagerRoleName || data.role === licenseReviewerRoleName) && !hasManageGlobalLicensesPermission) ||
          data.role === StructmasterUserRoleName ? (
            ''
          ) : (
            <span
              className="clickable-span strong"
              onClick={() => {
                setSelectedAssignment(data);
                setLicenseAttrVisible(true);
              }}
            >
              {t('UpdateRoleAttributes')}
            </span>
          ),
      },
    ],
    data: groupRolesState.value as ListState<RoleAssignmentSummary>,
    sortFields: [],
  } as TableModel<RoleAssignmentSummary>;

  const includeSearches = (q: Query) => {
    const nextQuery = clone<Query>(q);
    if (searchFilter.current && nextQuery.filter) {
      const scopeSearch = { and: [`contains(tolower(Scope/Title), '${searchFilter.current.toLowerCase()}')`] };
      if (isArray(nextQuery.filter)) {
        nextQuery.filter.push(scopeSearch);
      } else {
        nextQuery.filter = { ...nextQuery.filter, scopeSearch };
      }
    }
    return nextQuery;
  };

  const onSelectionChanged = (rowsKeys: string[]) => {
    setSelectedRowsLength(rowsKeys.length);
    return rowsKeys;
  };
  const onDeleteRow = async (item: RoleAssignmentSummary) => {
    let ignoreRoleForDelete = (item.role === licenseManagerRoleName || item.role === licenseReviewerRoleName) && !hasManageGlobalLicensesPermission;
    ignoreRoleForDelete = ignoreRoleForDelete || item.role === StructmasterUserRoleName;
    if (ignoreRoleForDelete) {
      return;
    }
    await groupRolesStore.delete(item.id).then(async () => {
      if (item.role === licenseManagerRoleName) {
        setSelectedAssignment(undefined);
        await licenseCreationAttributesStore.deleteByUserScope({
          identityId: item.identityId,
          scopeId: item.scopeId,
          identityType: IdentityType.Group,
        });
      }
    });
  };

  return (
    <div className="table-small-toolbar">
      <Spin spinning={checkingPermissions || groupRolesState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}>
        <TableView
          selectedRowsNumber={selectedRowsLength ?? 0}
          rowKey="id"
          title={t('RolesAssigned')}
          titlePadding={'0 40px'}
          canCreateNew
          onNewItem={() => setAddRolesVisible(true)}
          canDelete={true}
          onDeleteRow={(item: any) => onDeleteRow(item)}
          editLabel={t('EditSelected')}
          onSelectionChanged={onSelectionChanged}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            const augmentedQuery = includeSearches(query);
            setQuery(query);
            groupRolesStore.load(augmentedQuery);
          }}
          onRefresh={onRefresh}
          model={groupRolesState.errorMessage.value ? emptyTableModel : tableModel}
          error={
            groupRolesState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(groupRolesState.errorMessage.value) : t('GroupRolesTableError'))
          }
          rightToolbar={
            <>
              <li hidden={selectedRowsLength > 0} style={{ marginRight: 5 }}>
                <ValidatedSearch
                  className="main-table-search"
                  allowClear
                  placeholder={t('SearchScope')}
                  prefix={<SearchOutlined />}
                  style={{ width: '100%', minWidth: 200, marginTop: onMobile ? 24 : undefined, marginBottom: onMobile ? 24 : undefined }}
                  onSearch={(search: string) => {
                    if (search) {
                      searchFilter.current = search;
                    } else {
                      searchFilter.current = undefined;
                    }
                    const augmentedQuery = includeSearches(query);
                    groupRolesStore.load(augmentedQuery);
                  }}
                />
              </li>
            </>
          }
        />
        {addRolesVisible && (
          <AddGroupRoleModal
            groupType={GroupType.Default}
            groupName={groupName}
            organizationName={organizationName}
            isPartnerOrg={isPartnerOrg}
            groupId={groupId}
            groupOrganization={groupOrganizationId}
            onClose={(response, addAnother) => {
              if ((response && !addAnother) || !response) {
                setAddRolesVisible(false);
              }
              onRefresh();
            }}
          />
        )}
        {licenseAttrVisible && selectedAssignment && (
          <UpdateRoleAttributesDialog
            editLicenseManagerRoleAttributes={hasManageGlobalLicensesPermission && selectedAssignment.role === licenseManagerRoleName}
            scopeId={selectedAssignment.scopeId}
            identityId={selectedAssignment.identityId}
            identityType={IdentityType.Group}
            onClose={(newExpirationDate?: Date) => {
              if (newExpirationDate && selectedAssignment.expirationDate !== newExpirationDate) {
                groupRolesStore
                  .updateExpiration(selectedAssignment.id, { scopeId: selectedAssignment.scopeId, newExpirationDate: newExpirationDate })
                  .then(() => {
                    groupRolesStore.load(query);
                  });
              }
              setLicenseAttrVisible(false);
            }}
            roleExpirationDate={selectedAssignment.expirationDate}
          />
        )}
      </Spin>
    </div>
  );
};

export default GroupRolesTab;
